import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessLocationContext = createContext();

export const useBusinessLocation = () => {
  const context = useContext(BusinessLocationContext);
  if (!context) {
    throw new Error('useBusinessLocation must be used within a BusinessLocationProvider');
  }
  return context;
};

export const BusinessLocationProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for business location endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getBusinessLocationBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/business-locations/api/v1/business-locations`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/business-locations/api/v1/business-locations`;
    } else {
      throw new Error('Invalid login type for business location access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access business location data');
    }
  }, [loginType]);

  // ------------------------------------------------------------------
  // GET REQUESTS (READ)
  // ------------------------------------------------------------------

  // Get all locations for a specific business by registry number
  const getLocationsByRegistryNumber = useCallback(async (registryNumber) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBusinessLocationBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch locations';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessLocationBaseUrl]);

  // Get a specific location by location_id
  const getLocationById = useCallback(async (locationId) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBusinessLocationBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/location/${encodeURIComponent(locationId)}`
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get location';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessLocationBaseUrl]);

  // ------------------------------------------------------------------
  // POST REQUESTS (CREATE)
  // ------------------------------------------------------------------

  // Create a new business location
  const createLocation = useCallback(async (locationData) => {
    validateAccess();
    
    // Validation
    if (!locationData.location_id) {
      throw new Error('Location ID is required');
    }
    if (!locationData.business_registry_number) {
      throw new Error('Business registry number is required');
    }
    if (!locationData.location_address_1 || !locationData.location_city || 
        !locationData.location_country || !locationData.location_zip) {
      throw new Error('Missing required address fields');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBusinessLocationBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/location`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(locationData)
        }
      );
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to create location';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessLocationBaseUrl]);

  // ------------------------------------------------------------------
  // PUT REQUESTS (UPDATE)
  // ------------------------------------------------------------------

  // Update an existing location
  const updateLocation = useCallback(async (locationId, updateData) => {
    validateAccess();
    
    if (!locationId) {
      throw new Error('Location ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBusinessLocationBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/location/${encodeURIComponent(locationId)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        }
      );
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update location';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessLocationBaseUrl]);

  // ------------------------------------------------------------------
  // DELETE REQUESTS
  // ------------------------------------------------------------------

  // Delete a location
  const deleteLocation = useCallback(async (locationId) => {
    validateAccess();
    
    if (!locationId) {
      throw new Error('Location ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getBusinessLocationBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/location/${encodeURIComponent(locationId)}`,
        {
          method: 'DELETE'
        }
      );
      
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete location';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getBusinessLocationBaseUrl]);

  // ------------------------------------------------------------------
  // UTILITIES & HELPERS
  // ------------------------------------------------------------------

  // Get main location for a business
  const getMainLocation = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const result = await getLocationsByRegistryNumber(registryNumber);
      
      if (result.data && result.data.length > 0) {
        // Find the main location (location_main = true)
        const mainLocation = result.data.find(loc => loc.location_main === true);
        return mainLocation || result.data[0]; // Return first location if no main location is set
      }
      
      return null;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get main location';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getLocationsByRegistryNumber, validateAccess]);

  // Get visible locations for a business
  const getVisibleLocations = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const result = await getLocationsByRegistryNumber(registryNumber);
      
      if (result.data && result.data.length > 0) {
        // Filter only visible locations
        return result.data.filter(loc => loc.location_visibility === true);
      }
      
      return [];
    } catch (err) {
      const errorMessage = err.message || 'Failed to get visible locations';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getLocationsByRegistryNumber, validateAccess]);

  // Set a location as main
  const setMainLocation = useCallback(async (locationId) => {
    validateAccess();

    try {
      const updateData = {
        location_main: true
      };
      
      const result = await updateLocation(locationId, updateData);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to set main location';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [updateLocation, validateAccess]);

  // Toggle location visibility
  const toggleLocationVisibility = useCallback(async (locationId, isVisible) => {
    validateAccess();

    try {
      const updateData = {
        location_visibility: isVisible
      };
      
      const result = await updateLocation(locationId, updateData);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to toggle location visibility';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [updateLocation, validateAccess]);

  // Update location coordinates
  const updateLocationCoordinates = useCallback(async (locationId, latitude, longitude) => {
    validateAccess();

    if (latitude === undefined || longitude === undefined) {
      throw new Error('Latitude and longitude are required');
    }

    try {
      const updateData = {
        location_latitude: latitude,
        location_longitude: longitude
      };
      
      const result = await updateLocation(locationId, updateData);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update coordinates';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [updateLocation, validateAccess]);

  // Update location address
  const updateLocationAddress = useCallback(async (locationId, addressData) => {
    validateAccess();

    if (!addressData || Object.keys(addressData).length === 0) {
      throw new Error('Address data is required');
    }

    try {
      const updateData = {};
      
      if (addressData.address1) updateData.location_address_1 = addressData.address1;
      if (addressData.address2 !== undefined) updateData.location_address_2 = addressData.address2;
      if (addressData.city) updateData.location_city = addressData.city;
      if (addressData.country) updateData.location_country = addressData.country;
      if (addressData.zip) updateData.location_zip = addressData.zip;
      
      const result = await updateLocation(locationId, updateData);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update address';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [updateLocation, validateAccess]);

  // Format location data for display
  const formatLocationData = useCallback((location) => {
    return {
      ...location,
      fullAddress: `${location.location_address_1}${location.location_address_2 ? ', ' + location.location_address_2 : ''}, ${location.location_city}, ${location.location_country} ${location.location_zip}`,
      coordinates: {
        lat: location.location_latitude,
        lng: location.location_longitude
      },
      isMain: location.location_main || false,
      isVisible: location.location_visibility || false,
      sceneryType: location.location_scenery_type || 'N/A',
    };
  }, []);

  // Validate location data
  const validateLocationData = useCallback((locationData) => {
    const errors = [];

    if (!locationData.location_id) {
      errors.push({ field: 'location_id', message: 'Location ID is required' });
    }

    if (!locationData.business_registry_number) {
      errors.push({ field: 'business_registry_number', message: 'Business registry number is required' });
    }

    if (!locationData.location_address_1) {
      errors.push({ field: 'location_address_1', message: 'Address line 1 is required' });
    }

    if (!locationData.location_city) {
      errors.push({ field: 'location_city', message: 'City is required' });
    }

    if (!locationData.location_country) {
      errors.push({ field: 'location_country', message: 'Country is required' });
    }

    if (!locationData.location_zip) {
      errors.push({ field: 'location_zip', message: 'ZIP code is required' });
    }

    if (locationData.location_latitude !== undefined) {
      const lat = parseFloat(locationData.location_latitude);
      if (isNaN(lat) || lat < -90 || lat > 90) {
        errors.push({ field: 'location_latitude', message: 'Latitude must be between -90 and 90' });
      }
    }

    if (locationData.location_longitude !== undefined) {
      const lng = parseFloat(locationData.location_longitude);
      if (isNaN(lng) || lng < -180 || lng > 180) {
        errors.push({ field: 'location_longitude', message: 'Longitude must be between -180 and 180' });
      }
    }

    return errors;
  }, []);

  // Get location statistics for a business
  const getLocationStats = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const result = await getLocationsByRegistryNumber(registryNumber);
      
      if (result.data) {
        const locations = result.data;
        const mainLocation = locations.find(loc => loc.location_main === true);
        const visibleLocations = locations.filter(loc => loc.location_visibility === true);
        
        return {
          totalLocations: locations.length,
          visibleLocations: visibleLocations.length,
          hiddenLocations: locations.length - visibleLocations.length,
          hasMainLocation: !!mainLocation,
          mainLocationId: mainLocation?.location_id || null,
          locationTypes: [...new Set(locations.map(loc => loc.location_scenery_type).filter(Boolean))],
        };
      }
      
      return {
        totalLocations: 0,
        visibleLocations: 0,
        hiddenLocations: 0,
        hasMainLocation: false,
        mainLocationId: null,
        locationTypes: [],
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get location statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getLocationsByRegistryNumber, validateAccess]);

  // Health check for business location service
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master' 
        ? `${API_BASE_URL}/master/business-locations/health`
        : `${API_BASE_URL}/admin/business-locations/health`;
      
      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Get current endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master' 
        ? `${API_BASE_URL}/master/business-locations/api/v1/business-locations`
        : `${API_BASE_URL}/admin/business-locations/api/v1/business-locations`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,
    
    // Core Actions (Read)
    getLocationsByRegistryNumber,
    getLocationById,
    
    // Core Actions (Create)
    createLocation,
    
    // Core Actions (Update)
    updateLocation,
    setMainLocation,
    toggleLocationVisibility,
    updateLocationCoordinates,
    updateLocationAddress,
    
    // Core Actions (Delete)
    deleteLocation,
    
    // Utility Actions
    getMainLocation,
    getVisibleLocations,
    getLocationStats,
    checkHealth,

    // Helpers
    clearError,
    validateLocationData,
    formatLocationData,
    getEndpointInfo,
    
    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasLocationAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <BusinessLocationContext.Provider value={value}>
      {children}
    </BusinessLocationContext.Provider>
  );
};